import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Tooltip } from 'devextreme-react';
import moment from 'moment';
import './TrackingProgressBar.scss';
import { ModalNotes } from '../modal-notes/ModalNotes';
import { useApiTracking } from '../../../api/api.tracking';
import { newToast } from '../../index';

interface TrackingProgressBarProps {
  trackingData: any[];
  allStates: any[];
  currentOrder: any;
  onAdvance?: (orderId: number, tmId: number, tkeId: number) => void;
  onReject?: (orderId: number, tmId: number) => void;
  onTrackingUpdate?: (updatedData: any[]) => void;
  highlightedSampleId?: number | null;
}

export const TrackingProgressBar: React.FC<TrackingProgressBarProps> = ({ trackingData, allStates, currentOrder, onAdvance, onReject, onTrackingUpdate, highlightedSampleId }) => {
  // TODOS LOS HOOKS DEBEN IR AL PRINCIPIO - ANTES DE CUALQUIER RETURN

  // Estado para controlar qué líneas deben animarse
  const [animatingLines, setAnimatingLines] = useState<Set<string>>(new Set());

  // Estados para el modal de descarte
  const [discardModalVisible, setDiscardModalVisible] = useState<boolean>(false);
  const [selectedTrackingToDiscard, setSelectedTrackingToDiscard] = useState<any>(null);
  const [isDiscarding, setIsDiscarding] = useState<boolean>(false);

  const { discartTracking } = useApiTracking();

  // Efecto para animar las líneas cuando cambian los datos
  useEffect(() => {
    if (trackingData && trackingData.length > 0) {
      // Animar SOLO la línea que conecta el estado anterior con el estado actual
      trackingData.forEach((tracking) => {
        const currentStateIndex = allStates.findIndex((s) => s.Tke_Id === tracking.Tk_Tke_Id);

        // Solo animar la línea anterior al estado actual (la que se acaba de completar)
        if (currentStateIndex > 0) {
          const lineKey = `${tracking.Tk_Tm_Id}-line-${currentStateIndex - 1}`;

          setAnimatingLines((prev) => new Set(prev).add(lineKey));

          // Limpiar la animación después de que termine
          setTimeout(() => {
            setAnimatingLines((prev) => {
              const newSet = new Set(prev);
              newSet.delete(lineKey);
              return newSet;
            });
          }, 1000);
        }
      });
    }
  }, [trackingData, allStates]);

  // Agrupar por tipo de muestra usando useMemo para forzar recálculo
  const groupedByTm = useMemo(() => {
    if (!trackingData || trackingData.length === 0) {
      return {};
    }
    return trackingData.reduce((acc, item) => {
      if (!acc[item.Tk_Tm_Id]) {
        acc[item.Tk_Tm_Id] = [];
      }
      acc[item.Tk_Tm_Id].push(item);
      return acc;
    }, {} as Record<number, any[]>);
  }, [trackingData]);

  // TODOS LOS CALLBACKS DEBEN ESTAR ANTES DE LOS RETURNS CONDICIONALES
  const handleAdvanceClick = useCallback(
    (idTk: number, tkeId: number) => {
      if (onAdvance && currentOrder) {
        onAdvance(currentOrder.Ord_Id, idTk, tkeId);
      }
    },
    [onAdvance, currentOrder]
  );

  const handleRejectClick = useCallback(
    (tmId: number) => {
      // Encontrar el tracking correspondiente al tmId
      const tracking = trackingData.find((t) => t.Tk_Tm_Id === Number(tmId));
      if (tracking) {
        setSelectedTrackingToDiscard(tracking);
        setDiscardModalVisible(true);
      }
    },
    [trackingData]
  );

  const handleConfirmDiscard = useCallback(
    async (note: string) => {
      if (!selectedTrackingToDiscard || !note) {
        return;
      }

      setIsDiscarding(true);

      try {
        const discardData = {
          IdTk: selectedTrackingToDiscard.Tk_Id,
          IdOrd: selectedTrackingToDiscard.Tk_Ord_Id,
          IdTkEst: selectedTrackingToDiscard.Tk_Tke_Id,
          Notes: note,
        };

        const response = await discartTracking(discardData);

        // Verificar si la respuesta es válida
        if (response && response.length > 0) {
          // Actualizar los datos de tracking
          if (onTrackingUpdate) {
            onTrackingUpdate(response);
          }
          newToast('SUCCESS', 'Muestra descartada correctamente', 3);
          setDiscardModalVisible(false);
          setSelectedTrackingToDiscard(null);
        } else {
          newToast('ERROR', 'Error al descartar la muestra', 3);
        }
      } catch (error) {
        newToast('ERROR', 'Error al descartar la muestra', 3);
      } finally {
        setIsDiscarding(false);
      }
    },
    [selectedTrackingToDiscard, discartTracking, onTrackingUpdate]
  );

  const handleCancelDiscard = useCallback(() => {
    setDiscardModalVisible(false);
    setSelectedTrackingToDiscard(null);
  }, []);

  const calculateTimeDifference = useCallback((startDate: Date, endDate: Date) => {
    const diff = moment(endDate).diff(moment(startDate));
    const duration = moment.duration(diff);

    const days = Math.floor(duration.asDays());
    const hours = duration.hours();
    const minutes = duration.minutes();

    if (days > 0) {
      return `${days}d ${hours}h`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  }, []);

  // AHORA SÍ PUEDEN IR LOS RETURNS CONDICIONALES

  // Si no hay orden seleccionada, mostrar mensaje
  if (!currentOrder) {
    return (
      <div className='tracking-empty-state'>
        <h3 className='empty-title'>Tracking de Muestras</h3>
        <p className='empty-message'>Seleccione una orden para ver el estado de sus muestras</p>
      </div>
    );
  }

  // Si hay orden pero no hay tracking data, mostrar estados vacíos
  if (!trackingData || trackingData.length === 0) {
    return (
      <div className='tracking-progress-container'>
        <div className='tracking-row'>
          <div className='tracking-sample-info'>
            <div className='test-tube test-tube-large'>
              <div className='liquid' style={{ backgroundColor: '#ccc' }}></div>
            </div>
            <div className='sample-text'>
              <span className='sample-title'>Tipo Muestra</span>
              <span className='sample-label'>SIN MUESTRAS</span>
            </div>
          </div>

          <div className='tracking-progress'>
            {allStates.map((state, index) => (
              <div key={state.Tke_Id} className='progress-step'>
                <div className='step-node-container'>
                  <div className='step-node' id={`node-empty-${state.Tke_Id}`}>
                    <div className='step-label'>
                      <div className='state-name'>{state.Tke_Nombre.toUpperCase()}</div>
                    </div>
                  </div>
                  {index < allStates.length - 1 && <div className='step-line'></div>}
                </div>
              </div>
            ))}
          </div>

          <div className='tracking-action'>
            <div style={{ fontSize: '12px', color: '#999', fontStyle: 'italic' }}>Sin tracking disponible</div>
          </div>
        </div>
      </div>
    );
  }

  // Renderizar el componente principal
  return (
    <div className='tracking-progress-container'>
      {Object.entries(groupedByTm).map(([tmId, trackings]) => {
        const trackingsList = trackings as any[];
        const currentTracking = trackingsList[trackingsList.length - 1];
        const isDiscarded = currentTracking.Tk_Tke_Id === null;
        const currentStateIndex = allStates.findIndex((s) => s.Tke_Id === currentTracking.Tk_Tke_Id);
        const totalStates = allStates.length;
        const nextState = currentStateIndex < totalStates - 1 ? allStates[currentStateIndex + 1] : null;
        const isHighlighted = highlightedSampleId === Number(tmId);

        // Obtener información del descarte si existe
        let discardInfo: any = null;
        if (isDiscarded && currentTracking.MonitoreoTracking) {
          // Buscar el último registro en MonitoreoTracking (el descarte)
          const sortedMonitoreo = [...currentTracking.MonitoreoTracking].sort((a, b) => new Date(b.Mtt_Fecha).getTime() - new Date(a.Mtt_Fecha).getTime());
          discardInfo = sortedMonitoreo[0]; // El más reciente es el descarte
        }

        return (
          <div key={tmId} className={`tracking-row ${isHighlighted ? 'highlighted' : ''} ${isDiscarded ? 'discarded' : ''}`}>
            {/* Tubo y nombre de muestra */}
            <div className='tracking-sample-info'>
              <div className='test-tube test-tube-large'>
                <div className='liquid' style={{ backgroundColor: currentTracking.TipoMuestra.Tm_Color || '#888' }}></div>
              </div>
              <div className='sample-text'>
                <span className='sample-title'>Tipo Muestra</span>
                <span className='sample-label'>{currentTracking.TipoMuestra.Tm_Nombre.toUpperCase()}</span>
              </div>
            </div>

            {/* Contenido condicional: Si está descartada, mostrar info de descarte, si no, mostrar barra de progreso */}
            {isDiscarded ? (
              <div className='tracking-discard-info'>
                <div className='discard-header'>
                  <span className='discard-title'>MUESTRA DESCARTADA</span>
                </div>
                {discardInfo && (
                  <div className='discard-details'>
                    {/* Primera fila: Rechazada por y Fecha en la misma línea */}
                    <div className='discard-detail-row'>
                      <span className='detail-label'>Rechazada por:</span>
                      <span className='detail-value'>
                        {currentTracking.Usuario?.Us_Nombre} {currentTracking.Usuario?.Us_Apellido1} {currentTracking.Usuario?.Us_Apellido2}
                      </span>
                      <span className='detail-label'>Fecha:</span>
                      <span className='detail-value'>{moment(discardInfo.Mtt_Fecha).format('DD/MM/YYYY HH:mm')}</span>
                    </div>
                    {/* Segunda fila: Motivo (solo si existe) */}
                    {discardInfo.Mtt_Notas && (
                      <div className='discard-detail-row notes'>
                        <span className='detail-label'>Motivo:</span>
                        <span className='detail-value'>{discardInfo.Mtt_Notas}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className='tracking-progress'>
                {allStates.map((state, index) => {
                  const trackingsList = trackings as any[];

                  // Buscar si existe este estado en CUALQUIER tracking de esta muestra
                  // Ya que el tracking actual puede estar en un estado diferente
                  let monitoreoForThisState: any = null;
                  let trackingWithThisState: any = null;

                  // Buscar en todos los trackings de este tipo de muestra
                  for (const t of trackingsList) {
                    const monitoreo = t.MonitoreoTracking?.find((m: any) => m.Mtt_Tke_Id === state.Tke_Id);
                    if (monitoreo) {
                      monitoreoForThisState = monitoreo;
                      trackingWithThisState = t;
                      break;
                    }
                  }

                  const isCompleted = monitoreoForThisState !== null;
                  const isCurrent = state.Tke_Id === currentTracking.Tk_Tke_Id;

                  // Calcular tiempo transcurrido para mostrar en la línea DESPUÉS de este nodo
                  let lineTimeLabel = '';
                  if (isCompleted && index < allStates.length - 1) {
                    const nextState = allStates[index + 1];

                    // Buscar el monitoreo del siguiente estado
                    let nextMonitoreo: any = null;
                    for (const t of trackingsList) {
                      const monitoreo = t.MonitoreoTracking?.find((m: any) => m.Mtt_Tke_Id === nextState.Tke_Id);
                      if (monitoreo) {
                        nextMonitoreo = monitoreo;
                        break;
                      }
                    }

                    if (nextMonitoreo) {
                      // Ambos estados completados: calcular tiempo entre ellos
                      lineTimeLabel = calculateTimeDifference(new Date(monitoreoForThisState.Mtt_Fecha), new Date(nextMonitoreo.Mtt_Fecha));
                    } else if (isCurrent) {
                      // Estado actual sin siguiente completado: tiempo desde que llegó hasta ahora
                      lineTimeLabel = calculateTimeDifference(new Date(monitoreoForThisState.Mtt_Fecha), new Date());
                    }
                  }

                  return (
                    <div key={state.Tke_Id} className='progress-step'>
                      <div className='step-node-container'>
                        <div className={`step-node ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''}`} id={`node-${tmId}-${state.Tke_Id}`}>
                          {isCompleted && <span className='check'></span>}

                          {/* Leyenda centrada arriba del nodo (solo visible en desktop) */}
                          <div className='step-label'>
                            <div className={`state-name ${isCurrent ? 'current' : ''}`}>{state.Tke_Nombre.toUpperCase()}</div>
                            {/* Mostrar fecha si el estado está completado (tiene monitoreo) */}
                            {monitoreoForThisState && <div className='state-date'>{moment(monitoreoForThisState.Mtt_Fecha).format('DD/MM/YY HH:mm')}</div>}
                          </div>
                        </div>
                        {index < allStates.length - 1 && (
                          <div
                            className={`step-line 
                          ${isCompleted && index < currentStateIndex ? 'completed' : ''} 
                          ${animatingLines.has(`${tmId}-line-${index}`) ? 'animate' : ''}`}>
                            {/* Label de tiempo en el centro de la línea */}
                            {lineTimeLabel && (
                              <div className={`line-time-label ${isCurrent ? 'current-time' : ''}`}>
                                <span className='time-icon'>⏱</span> {lineTimeLabel}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Tooltip del nodo con información completa */}
                      <Tooltip target={`#node-${tmId}-${state.Tke_Id}`} showEvent='mouseenter' hideEvent='mouseleave' position='top'>
                        <div className='toolTipFilter'>
                          <div>
                            <strong>Estado:</strong> {state.Tke_Nombre}
                          </div>
                          {monitoreoForThisState && trackingWithThisState && (
                            <>
                              <div>
                                <strong>Fecha:</strong> {moment(monitoreoForThisState.Mtt_Fecha).format('DD/MM/YYYY HH:mm')}
                              </div>
                              <div>
                                <strong>Usuario:</strong> {trackingWithThisState.Usuario.Us_Nombre} {trackingWithThisState.Usuario.Us_Apellido1} {trackingWithThisState.Usuario.Us_Apellido2}
                              </div>
                            </>
                          )}
                          {!monitoreoForThisState && <div style={{ fontStyle: 'italic', color: '#999' }}>Pendiente</div>}
                        </div>
                      </Tooltip>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Botones de acción */}
            <div className='tracking-action'>
              {isDiscarded ? (
                <div className='discarded-status'>
                  <Button type='danger' stylingMode='outlined' icon='close' disabled text='DESCARTADA' />
                </div>
              ) : currentStateIndex < totalStates - 1 ? (
                <>
                  <div id={`btnReject${tmId}`}>
                    <Button type='danger' stylingMode='contained' icon='close' onClick={() => handleRejectClick(Number(tmId))} />
                    <Tooltip target={`#btnReject${tmId}`} showEvent='mouseenter' hideEvent='mouseleave' position='top'>
                      <div className='tooltip-reject'>
                        <strong>Descartar muestra</strong>
                        <div>Marcar como descartada</div>
                      </div>
                    </Tooltip>
                  </div>

                  <div className='action-separator'></div>

                  <div id={`btnAdvance${tmId}`}>
                    <Button type='default' stylingMode='contained' icon='chevronnext' onClick={() => handleAdvanceClick(currentTracking.Tk_Id, currentTracking.Tk_Tke_Id)} />
                    <Tooltip target={`#btnAdvance${tmId}`} showEvent='mouseenter' hideEvent='mouseleave' position='top'>
                      <div className='tooltip-advance'>
                        <strong>Avanzar muestra a:</strong>
                        <div>{nextState?.Tke_Nombre}</div>
                      </div>
                    </Tooltip>
                  </div>
                </>
              ) : (
                <Button type='success' stylingMode='contained' icon='check' disabled />
              )}
            </div>
          </div>
        );
      })}

      {/* Modal de notas para descartar muestra */}
      <ModalNotes visible={discardModalVisible} type='Tracking' currentNote='' onConfirm={handleConfirmDiscard} onCancel={handleCancelDiscard} />
    </div>
  );
};
